import React, { useMemo, useRef, useState, useEffect } from 'react'
import Chart from 'react-apexcharts'
import ApexCharts from 'apexcharts'
import Select from 'react-select'
import removeAccents from 'remove-accents'
import { IDadosGraficosResultadosPorDimensaoPesquisaModel } from '../helpers/AnaliseResultadoHelper'
import { ISelectModel } from '../../../../modules/services/core/_models'

type Props = {
  dadosGraficosPesquisa: IDadosGraficosResultadosPorDimensaoPesquisaModel | undefined
  dimensoes: ISelectModel[]
  dimensaoSelecionada: ISelectModel | undefined
  setDimensao: Function
  loading: boolean
  letraSelecionada: string
  setLetraSelecionada: React.Dispatch<React.SetStateAction<string>>
}

const GraficoResultadosParticipantesPorCategoria = ({
  dadosGraficosPesquisa,
  dimensoes,
  dimensaoSelecionada,
  setDimensao,
  letraSelecionada,
  setLetraSelecionada
}: Props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [buscouDados, setBuscouDados] = useState(false)
  const chartRef = useRef<any>(null)
  const letras = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  const stackType: 'normal' | '100%' = '100%'

  useEffect(() => {
    if (dadosGraficosPesquisa) {
      setBuscouDados(true)
    }
  }, [dadosGraficosPesquisa])

  const filteredCategories = useMemo(() => {
    if (!dadosGraficosPesquisa?.Categories) return []
    if (letraSelecionada === 'TODOS') return dadosGraficosPesquisa.Categories
    return dadosGraficosPesquisa.Categories.filter((nome) =>
      removeAccents(nome).toUpperCase().startsWith(letraSelecionada)
    )
  }, [letraSelecionada, dadosGraficosPesquisa])

  const filteredSeries = useMemo(() => {
    if (!dadosGraficosPesquisa?.Series) return []
    const indices = dadosGraficosPesquisa.Categories
      .map((nome, index) =>
        letraSelecionada === 'TODOS' ||
        removeAccents(nome).toUpperCase().startsWith(letraSelecionada)
          ? index
          : -1
      )
      .filter((i) => i !== -1)

    return dadosGraficosPesquisa.Series.map((serie) => ({
      name: serie.name,
      color: serie.color,
      data: indices.map((i) => serie.data[i])
    }))
  }, [letraSelecionada, dadosGraficosPesquisa])

  const options = {
    colors: dadosGraficosPesquisa?.Colors ?? [],
    series: filteredSeries,
    chart: {
      id: 'grafico-participantes',
      stacked: true,
      stackType: stackType,
      toolbar: { show: false }
    },
    plotOptions: {
      bar: { horizontal: true }
    },
    stroke: {
      width: 1,
      colors: ['#fff']
    },
    xaxis: {
      categories: filteredCategories
    },
    fill: { opacity: 1 },
    legend: {
      offsetX: 40,
      inverseOrder: true
    }
  }

  const downloadChart = (format: 'png' | 'jpeg') => {
    ApexCharts.exec('grafico-participantes', 'dataURI').then((res: { imgURI: string }) => {
      const link = document.createElement('a')
      link.href = res.imgURI
      link.download = `grafico_participantes.${format}`
      link.click()
    })
  }

  const ALTURA_POR_ITEM = 30
  const ALTURA_MINIMA = 100
  const ALTURA_MAXIMA = 120000

  const chartHeight = Math.min(Math.max(filteredCategories.length * ALTURA_POR_ITEM, ALTURA_MINIMA), ALTURA_MAXIMA)

  const isLoading = buscouDados && (!dadosGraficosPesquisa || !dadosGraficosPesquisa.Series?.length)

  return (
    <div className={`card m-0 p-0 ${dadosGraficosPesquisa ? '' : 'd-none'}`}>
      <div className='card-body m-0 p-0'>
        <div className='row justify-content-between m-0 p-5 pb-3'>
          <div className='fv-row col-lg-4'>
            <label className='form-label text-gray-800'>Selecione a dimensão</label>
            {dimensoes.length > 0 && (
              <Select
                placeholder='Selecione...'
                className='form-control-custom-select'
                noOptionsMessage={() => 'Nenhuma opção encontrada'}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    border: 'none',
                    backgroundColor: '$input-solid-bg',
                    borderRadius: '7px',
                    height: '3.4rem',
                    color: '#5e6278',
                    fontSize: '1.15rem'
                  }),
                  menu: (baseStyles) => ({
                    ...baseStyles,
                    backgroundColor: '#F9F9F9'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    color: state.isFocused ? 'black' : '$input-solid-color'
                  })
                }}
                options={dimensoes.map((data) => ({
                  value: data.Id,
                  label: data.Nome
                }))}
                value={
                  dimensaoSelecionada
                    ? { value: dimensaoSelecionada.Id, label: dimensaoSelecionada.Nome }
                    : null
                }
                isSearchable
                onChange={(e: any) => setDimensao(e)}
              />
            )}
          </div>

          {/* Botão ☰ de exportação */}
          <div className='fv-row col-lg-1 d-flex align-items-end justify-content-lg-end'>
            <div className='dropdown'>
              <button
                className='btn btn-light btn-sm dropdown-toggle'
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                ☰
              </button>
              <div
                className={`dropdown-menu dropdown-menu-end ${dropdownOpen ? 'show' : ''}`}
                style={{ minWidth: '160px' }}
              >
                <button className='dropdown-item' onClick={() => downloadChart('png')}>
                  Baixar PNG
                </button>
                <button className='dropdown-item' onClick={() => downloadChart('jpeg')}>
                  Baixar JPEG
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Botões de filtro por letra */}
        <div className='d-flex flex-wrap gap-2 px-5 pt-2'>
          {letras.map((letra) => (
            <button
              key={letra}
              onClick={() => setLetraSelecionada(letra)}
              className={`btn btn-sm ${
                letraSelecionada === letra ? 'btn-primary' : 'btn-light'
              }`}
            >
              {letra}
            </button>
          ))}
          <button
            onClick={() => setLetraSelecionada('TODOS')}
            className={`btn btn-sm ${
              letraSelecionada === 'TODOS' ? 'btn-primary' : 'btn-light'
            }`}
          >
            Todos
          </button>
        </div>

        <div className='row justify-content-center m-0 p-0'>
          {isLoading ? (
            <div className='text-center py-10'>
              <span className='spinner-border text-primary' role='status'></span>
              <div className='mt-3'>Carregando gráfico...</div>
            </div>
          ) : filteredCategories.length > 0 ? (
            <Chart
              ref={chartRef}
              options={options}
              series={filteredSeries}
              type='bar'
              height={chartHeight}
            />
          ) : (
            <div className='text-center text-muted py-10'>
              Nenhum participante encontrado para a letra <strong>{letraSelecionada}</strong>.
            </div>
          )}
        </div>

      </div>
    </div>
  )
}

export { GraficoResultadosParticipantesPorCategoria }
